import { IData } from '../virtual-data/VirtualData';
import {
  DataAggregateVO,
  DEFAULT_FIELD_VALUES_FILTER_MAX_FETCHED_ITEMS,
  FinalClientTableConfig,
  FinalTableApiConfig,
  FinalTableColumn,
  FinalTableConfig,
  FinalTableEventType,
  FinalTableProfileData,
  FinalTableProps,
  FinalTableType,
  FOOTER,
  GROUP_PREFIX,
  GroupStatus,
  LoadedMap,
  SectionRange,
  SortInfo,
} from './FinalTableProps';
import {
  AreaKey,
  ColumnInfo,
  ConditionGroupVO,
  FieldValuesFilterData,
} from '../rows-data/types';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Index, IndexRange, InfiniteLoader } from 'react-virtualized';
import { RowsDataConfig, useRowsData } from '../rows-data/useRowsData';
import { throttle } from 'lodash-es';
import {
  DataGroupVO,
  DataRequestType,
  DataRequestVO,
  DataResponseVO,
} from '@mc/data-filter-react';
import {
  arrayMakeSpace,
  computeSort,
  getColumnByField,
  getColumnField,
  getColumnsView,
  getFinalTableDataKey,
  getFinalTableKey,
  getVisibleColumns,
  getVisibleColumnsKey,
} from './utils';
import { getLoadDataError, LoadDataApiError } from '@mc/axios-api';
import { FinalTableDataContext } from './FinalTableDataProvider';
import {
  getInitialTableConfig,
  useTableProfiles,
  useTableSettings,
  useTableViews,
} from './TableSettings';
import { DataFieldValuesRequestVO } from '@mc/data-filter-react';
import {
  finalTableApiWrapper,
  FinalTableApiWrapperConfig,
} from './finalTableApiWrapper';
import { useFinalTableData } from './useFinalTableData';
import { LAST_FILTER_ID, useFilters } from '../filter/useFilters';

type SortMap = { [key: string]: SortInfo };

export const NULL_VALUE = '¬null¬';

type ChangeArray = {
  fromIndex: number;
  count: number;
  // Pokud true, jedná se o vkládání count na fromIndex, při false se jedná o smazání count z indexu fromIndex
  insert: boolean;
};

type GroupFilter = { [key: string]: object };

type ClientApiData<DATA extends IData<ID>, FILTER, ID> = {
  data: DATA[];
  config: FinalClientTableConfig<DATA, FILTER, ID>;
  apiWrapperConfig?: FinalTableApiWrapperConfig<DATA>;
};
export const useFinalClientTable = <DATA extends IData<ID>, FILTER, ID>(
  finalTableKey: AreaKey,
  data: DATA[],
  config: FinalClientTableConfig<DATA, FILTER, ID>,
  apiWrapperConfig?: FinalTableApiWrapperConfig<DATA>
): FinalTableProps<DATA, FILTER, ID> => {
  const finalTableData = useFinalTableData(finalTableKey, {});
  const firstRenderRef = useRef<boolean>(true);

  // Serializované verze filtrů pro stabilní porovnání v useEffect
  const hardFilterStr = useMemo(
    () => JSON.stringify(config.hardFilter),
    [config.hardFilter]
  );
  const extendedFilterStr = useMemo(
    () => JSON.stringify(config.initialConfig?.initialExtendedFilter),
    [config.initialConfig?.initialExtendedFilter]
  );

  const clientApiDataRef = useRef<ClientApiData<DATA, FILTER, ID> | undefined>(
    undefined
  );
  clientApiDataRef.current = {
    data,
    config,
    apiWrapperConfig,
  };

  const api = <DATA, FILTER>(
    request: DataRequestVO<FILTER>,
    apiConfig?: FinalTableApiConfig
  ): Promise<DataResponseVO<DATA>> => {
    return finalTableApiWrapper<DATA, FILTER>(
      // @ts-ignore
      clientApiDataRef.current.data,
      // @ts-ignore
      clientApiDataRef.current.config.columns,
      // @ts-ignore
      clientApiDataRef.current.config.filters?.filtersDef ?? [],
      // @ts-ignore
      clientApiDataRef.current.apiWrapperConfig,
      // @ts-ignore
      clientApiDataRef.current.config.filters?.type
    )(request, apiConfig);
  };

  useEffect(() => {
    if (!firstRenderRef.current) {
      finalTableData.refetch();
    } else {
      firstRenderRef.current = false;
    }
  }, [data, hardFilterStr, extendedFilterStr]);

  const finalTableConfig: FinalTableConfig<DATA, FILTER, ID> = {
    ...config,
    api,
  };

  return useFinalTable<DATA, FILTER, ID>(finalTableKey, finalTableConfig);
};

export const useFinalTable = <DATA extends IData<ID>, FILTER, ID>(
  finalTableKey: AreaKey,
  config: FinalTableConfig<DATA, FILTER, ID>
): FinalTableProps<DATA, FILTER, ID> => {
  const firstRenderRef = useRef<boolean>(true);
  const finalTableDataContext = useContext(FinalTableDataContext);
  if (!finalTableDataContext.initByProvider) {
    throw new Error('Chybí FinalTableDataProvider, obalte jím aplikaci');
  }

  const [profiles] = useTableProfiles(config.settingsKey);
  const [views] = useTableViews(config.settingsKey);
  const { savedFilters } = useFilters(config.settingsKey);
  const [tableSettings] = useTableSettings(config.settingsKey);

  const finalTableKeyDataCacheKeyDataStr = getFinalTableKey(finalTableKey);

  // Ref pro seznam chyb načtení dat
  const errorsRef = useRef<LoadDataApiError[]>([]);
  // Cache načtených objektů - ID na datový objekt řádku
  const loadedMap = useRef<LoadedMap<DATA>>({});

  // Ref pro kontrolu fetch pro první úroveň - jestli se do vracení dat nezměnil stav zobrazení filtru apod. Pokud ano, výsledek fetche neodpovídá stavu a zahodíme ho, vlastně počítadlo pro ROOT fetch
  const fetchRef = useRef<number>(0);
  // Mapa id skupiny na index řádku skupiny pro který se načítají data
  const groupFetchDataRef = useRef<{ [key: string]: number | undefined }>({});

  let globalData = finalTableDataContext.getItem(
    finalTableKeyDataCacheKeyDataStr
  );

  // Počet zobrazovaných řádků, tedy skupiny i datové řádky co "jsou vidět"
  // const rowsCountRef = useRef<number | undefined>(undefined);
  // Reálný počet datových řádků odpovídající filtru
  // const dataRowsCountRef = useRef<number | undefined>(undefined);
  // Ref pro depth řádku - když máme seskupené dle dat, tak potomci mají o úroveň více
  // const depthRef = useRef<number[]>([]);
  // Ref pro status každého řádku - rozbalený, zabelený, načítají se data pro potomky (chtěl ho někdo rozbalit)
  // const groupStatusRef = useRef<GroupStatus[]>([]);
  // Ref pro určení zda daný řádek je seskupovací (group) řádek
  // const groupRef = useRef<boolean[]>([]);
  // Ref pro identifikátory řádků tabulky (jak datový tak group řádek)
  // const idsRef = useRef<ID[]>([]);
  // Cache načtených objektů - ID na datový objekt řádku
  // const groupLoadedMap = useRef<LoadedMap<DATA>>({});

  // ---

  /**
   * Vytvoření INIT konfigurace tabulky - to co je předáno na vstupu + na to aplikace profilu, pokud nějaký je.
   */
  const initialConfig: RowsDataConfig = useMemo(() => {
    let newConfig: RowsDataConfig = {
      ...config.initialConfig,
    };
    newConfig.initialFilter = {
      ...newConfig.initialFilter,
      filterDefs: config.filters?.filtersDef || [],
    };

    // Přednost má poslední nastavení pohledu tabulky, až poté případný vybraný profil (...protože pokud by bylo poslední nastavení, tak je již nad profilem a případně i změněné neuložené)
    if (
      config.settingsKey &&
      config.autoSaveSettings &&
      tableSettings.profileData
    ) {
      newConfig = {
        ...newConfig,
        ...getInitialTableConfig(tableSettings.profileData),
      };
    } else if (profiles.activeProfile) {
      newConfig = {
        ...newConfig,
        ...getInitialTableConfig(profiles.activeProfile.profile),
      };
    }

    // Viditelnost položek filtru, pokud je v posledním nastavení tabulky
    if (
      config.settingsKey &&
      config.autoSaveSettings &&
      tableSettings.filterData
    ) {
      newConfig = {
        ...newConfig,
        initialVisibleFields: tableSettings.filterData.visibleFields,
      };
    }

    // Initial filtr, pokud je přepnutý pohled, případně posledně uložený filtr, pokud to je na tabulce zapnuté v config
    if (views.activeView?.view) {
      newConfig.initialFilter = {
        ...newConfig.initialFilter,
        // @ts-ignore
        filterDefs: config.filters?.filtersDef,
        formFilterParam: views.activeView.view.filter ?? [],
        filterType: config.filters?.type,
      };
    } else if (
      firstRenderRef.current &&
      config.autoUseLastFilter &&
      savedFilters.length > 0
    ) {
      const lastFilter = savedFilters.find((x) => x.id === LAST_FILTER_ID);
      if (lastFilter) {
        console.log('@@@@@@@@@@@@@@@', lastFilter);

        newConfig.initialFilter = {
          ...newConfig.initialFilter,
          // @ts-ignore
          filterDefs: config.filters?.filtersDef,
          formFilterParam: lastFilter.filter,
          filterType: config.filters?.type,
        };

        newConfig.initialVisibleFields = lastFilter.visibleFields;
      }
    }

    return newConfig;
  }, [config.initialConfig]);

  const rowsData = useRowsData<FILTER, ID>(finalTableKey, {
    ...initialConfig,
  });

  const checkedIdsRef = useRef<ID[]>(rowsData.rows.checkedIds);
  checkedIdsRef.current = rowsData.rows.checkedIds;
  const setCheckedIdsRef = useRef(rowsData.rows.setCheckedIds);
  setCheckedIdsRef.current = rowsData.rows.setCheckedIds;

  // Serializované verze filtrů pro stabilní porovnání v useEffect
  const hardFilterStr = useMemo(
    () => JSON.stringify(config.hardFilter),
    [config.hardFilter]
  );
  const extendedFilterStr = useMemo(
    () => JSON.stringify(rowsData.filter.queryExtendedFilter),
    [rowsData.filter.queryExtendedFilter]
  );

  const finalTableKeyDataCacheKeyStr =
    finalTableKeyDataCacheKeyDataStr +
    `|hardFilter:${hardFilterStr}` +
    `|extendedFilter:${extendedFilterStr}`;

  useEffect(() => {
    finalTableDataContext.registerEvents(finalTableKey, (event) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`FinalTable - event:`, finalTableKey, event);
      }
      switch (event.type) {
        case FinalTableEventType.REFETCH:
          resetRowsCache({
            dataRow: true,
            groupRow: true,
            footer: true,
          });
          // globalData se nulují v FinalTableDataProvider, kde je REFRESH zpracován před tímto
          setTimeout(() => {
            setFilter(rowsData.filter.formFilter, rowsData.filter.queryFilter);
            resetVirtualTableCache();
          }, 1);
          break;
        case FinalTableEventType.INVALIDATE_ALL:
          resetRowsCache({
            dataRow: true,
            groupRow: true,
            footer: true,
          });
          resetVirtualTableCache();
          break;
        case FinalTableEventType.INVALIDATE_FOOTER:
          resetRowsCache({ footer: true });
          break;
        case FinalTableEventType.INVALIDATE_DATA_ROWS:
          if (event.ids === undefined) {
            resetRowsCache({ dataRow: true });
          } else {
            event.ids.forEach((id) => {
              delete loadedMap.current[`${id}`];
            });
          }
          resetVirtualTableCache();
          break;
        case FinalTableEventType.INSERT_ROWS:
          // Vkládání řádků se děje JEN při zobrazení datových řádků, nikoli skupin
          if (
            event.ids !== undefined &&
            rowsData.grouping.grouping.length === 0
          ) {
            let globalData = finalTableDataContext.getItem(
              finalTableKeyDataCacheKeyDataStr
            );

            const rowIds = event.ids;
            const index =
              lastRenderedSection.current?.startIndex !== undefined
                ? lastRenderedSection.current.startIndex > 0 &&
                  lastRenderedSection.current.startIndex + 1 <
                    globalData.ids.length
                  ? lastRenderedSection.current.startIndex + 1
                  : lastRenderedSection.current.startIndex
                : 0;
            const length = rowIds.length;

            arrayMakeSpace(globalData.group, index, length);
            arrayMakeSpace(globalData.groupStatus, index, length);
            arrayMakeSpace(globalData.depth, index, length);
            arrayMakeSpace(globalData.ids, index, length);
            globalData.rowsCount = (globalData.rowsCount || 0) + length;

            for (let a = index; a < index + length; a++) {
              globalData.group[a] = false;
              // globalData.groupStatus[a] = GroupStatus.;
              globalData.depth[a] = 0;
              globalData.ids[a] = rowIds[a - index];
            }

            const change: ChangeArray = {
              fromIndex: index,
              count: length,
              insert: true,
            };
            arrayUpdated(change);

            setRerender(new Date().getMilliseconds());
          }
          break;
        case FinalTableEventType.INVALIDATE_GROUP_ROWS:
          if (event.ids === undefined) {
            resetRowsCache({ groupRow: true });
          } else {
            event.ids.forEach((id) => {
              delete loadedMap.current[`${GROUP_PREFIX}${id}`];
            });
          }
          resetVirtualTableCache();
          break;
      }
    });
    return () => {
      finalTableDataContext.unregisterEvents(finalTableKey);
    };
  }, [
    finalTableKeyDataCacheKeyStr,
    rowsData.grouping.grouping,
    rowsData.filter.formFilter,
    rowsData.filter.queryFilter,
    hardFilterStr,
    extendedFilterStr,
  ]);

  const renderTypeRef = useRef<FinalTableType>(config.type || 'ids');
  const [loading, setLoading] = useState(true);

  const hardFilterFirstRenderRef = useRef<boolean>(true);
  useEffect(() => {
    if (hardFilterFirstRenderRef.current) {
      hardFilterFirstRenderRef.current = false;
    } else {
      setLoading(true);
    }
  }, [hardFilterStr]);

  const extendedFilterFirstRenderRef = useRef<boolean>(true);
  useEffect(() => {
    if (extendedFilterFirstRenderRef.current) {
      extendedFilterFirstRenderRef.current = false;
    } else {
      setLoading(true);
    }
  }, [extendedFilterStr]);

  const infiniteLoaderRef = useRef<InfiniteLoader | undefined>(undefined);

  // Pro rerender komponenty tabnulky - mění se vždy když se změní data, existuje kvůli tomu, že ostatní věci měníme např. jen do ref apod.
  const [rerender, setRerender] = useState<number>(0);
  const [refetchFooter, setRefetchFooter] = useState<number>(0);

  const throttleTime = 1000;

  const isRowLoadedByIndex = (index: number) => {
    let val = getLoadedItemByIndex(index);

    if (val === undefined) {
      return false;
    } else {
      return true;
    }
  };

  const isRowLoaded = ({ index }: Index) => {
    return isRowLoadedByIndex(index);
  };

  // Viditelné sloupce
  let columnsView = useMemo(() => {
    return getColumnsView(
      rowsData.grouping.grouping,
      rowsData.columns.columns,
      config
    );
  }, [rowsData.grouping.grouping, rowsData.columns.columns, config]);
  const visibleFields = config.readOnlyVisibleColumns
    ? getVisibleColumns(columnsView)
    : undefined;

  // ---
  // REF - nunté např. pto throttle callbacky apod.
  const groupingRef = useRef<DataGroupVO[]>(rowsData.grouping.grouping);
  const aggregateRef = useRef<DataAggregateVO[]>(rowsData.grouping.aggregate);
  const queryFilterRef = useRef<FILTER>(rowsData.filter.queryFilter);
  const visibleFieldsRef = useRef<string[] | undefined>(visibleFields);
  const fieldValuesFilterRef = useRef<FieldValuesFilterData>(
    rowsData.filter.fieldValuesFilter
  );
  const extendedFilterRef = useRef<ConditionGroupVO | undefined>(
    rowsData.filter.queryExtendedFilter
  );

  queryFilterRef.current = rowsData.filter.queryFilter;
  groupingRef.current = rowsData.grouping.grouping;
  aggregateRef.current = rowsData.grouping.aggregate;
  visibleFieldsRef.current = visibleFields;
  extendedFilterRef.current = rowsData.filter.queryExtendedFilter;

  // ---

  // Načítání dat - první úroveň - vždy při znevalidnění (např. jiný sort, group apod.), nebo pokud nejsou žádná data
  // console.log('####sortedVisibleColumnIdsKey', rowsData.columns.columns);
  const sortedVisibleColumnIdsKey = getVisibleColumnsKey(
    rowsData.columns.columns,
    config.columns,
    rowsData.grouping.grouping
  );

  // ---

  const lastRenderedSection = useRef<SectionRange | undefined>(undefined);

  const doRowsFetch = () => {
    switch (renderTypeRef.current) {
      case 'ids':
        doRowsFetchByIds();
        break;
      case 'pagination':
      case 'index':
        throw new Error('Not implemented');
    }
  };

  const setLoadedItemById = (groupRow: boolean, id: any, item: DATA) => {
    if (groupRow) {
      loadedMap.current[`${GROUP_PREFIX}${id}`] = item;
    } else {
      loadedMap.current[`${id}`] = item || null;
    }
  };

  useEffect(() => {
    const hasAggregateCols =
      rowsData.grouping.aggregate.filter(
        (x) => !rowsData.grouping.grouping.find((y) => y.field === x.field)
      ).length > 0;

    if (rowsData.footer.showFooter && hasAggregateCols) {
      config
        .api(
          {
            requestType: DataRequestType.SUMMARIZE,
            grouping: rowsData.grouping.grouping,
            filter: rowsData.filter.queryFilter,
            fieldValuesFilter: rowsData.filter.fieldValuesFilter,
            // @ts-ignore
            aggregate: rowsData.grouping.aggregate.map((x) => ({
              field: x.field,
              type: x.aggregate.toUpperCase(),
            })),
            visibleFields: visibleFields,
            extendedFilter: rowsData.filter.queryExtendedFilter,
          },
          { hardFilter: config.hardFilter }
        )
        .then((result) => {
          loadedMap.current[FOOTER] = result.rows ? result.rows[0] : null;
          setRerender(new Date().getMilliseconds());
        })
        .catch((error) => {
          errorsRef.current.push(getLoadDataError(error));
          setRerender(new Date().getMilliseconds());
        });
    }
  }, [
    rowsData.footer.showFooter,
    rowsData.grouping.grouping,
    rowsData.grouping.aggregate,
    rowsData.filter.formFilter,
    rowsData.filter.fieldValuesFilter,
    rowsData.filter.lastFilterChange,
    hardFilterStr,
    extendedFilterStr,
    sortedVisibleColumnIdsKey,
    refetchFooter,
    finalTableKeyDataCacheKeyStr,
  ]);

  // Následující funkce potřebuje různá data a ta musí být v ref, protože někdy (např. kvůli throttle) tam nejsou správná (v rowsData)
  // Proto všechna data pro tuto funkci bereme z různých REF
  const doRowsFetchByIds = useCallback(
    throttle(
      async () => {
        // Vytvoříme se dvě okénka - větší (start-250 - stop+250) a menší (start-200 - stop+200)
        // Pokud v menším okénku chybí data a je nutné něco načíst, načteme data pro větší okénko
        if (lastRenderedSection.current) {
          const start2 = Math.max(
            0,
            lastRenderedSection.current.startIndex - 200
          );
          const stop2 = Math.min(
            lastRenderedSection.current?.stopIndex + 200,
            globalData.rowsCount ?? 0
          );

          let range2NeedFetch = false;
          for (let a = start2; a < stop2; a++) {
            if (!isRowLoaded({ index: a })) {
              range2NeedFetch = true;
              break;
            }
          }

          if (range2NeedFetch) {
            const start = Math.max(
              0,
              lastRenderedSection.current.startIndex - 250
            );
            const stop = Math.min(
              lastRenderedSection.current?.stopIndex + 250,
              globalData.rowsCount ?? 0
            );

            // Připravení seznmů id pro načtení dat ze serveru
            // Základní seznam id pro datové řádky - jako klíč do mapy je -1
            // Další seznamy pro jednotlivé úrovně zanoření skupin
            // Mapa depth na seznam id pro načtení, depth -1 jsou základní datové řádky, ostatní (0 - n) jsou skupiny
            const idsForLoadMap: { [key: number]: ID[] } = {};
            for (let a = start; a < stop; a++) {
              let id = globalData.ids[a];
              if (!isRowLoadedByIndex(a)) {
                const depth = isGroupByIndex(a) ? getDepthByIndex(a) : -1;
                let ids = idsForLoadMap[depth];
                if (!ids) {
                  ids = [];
                  idsForLoadMap[depth] = ids;
                }
                ids.push(id);
              }
            }

            // Fetch dat - základní datové řádky
            if (idsForLoadMap[-1]?.length > 0) {
              const idsForLoad = idsForLoadMap[-1];

              config
                .api(
                  {
                    requestType: DataRequestType.DATA,
                    filter: queryFilterRef.current,
                    fieldValuesFilter: fieldValuesFilterRef.current,
                    idsRequest: {
                      // @ts-ignore
                      ids: idsForLoad,
                    },
                    visibleFields: visibleFieldsRef.current,
                    extendedFilter: extendedFilterRef.current,
                  },
                  { hardFilter: config.hardFilter }
                )
                .then((result) => {
                  const resultMap: { [key: string]: DATA } = {};
                  // @ts-ignore
                  result.rows?.forEach((item) => {
                    // @ts-ignore
                    resultMap[`${item.id}`] = item;
                  });

                  const deletedIdSet = new Set();
                  idsForLoad.forEach((id) => {
                    const item = resultMap[`${id}`];
                    if (item === undefined) {
                      deletedIdSet.add(`${id}`);
                    }
                    setLoadedItemById(false, id, item || null);
                  });

                  if (deletedIdSet.size > 0) {
                    const currentCheckedIds = checkedIdsRef.current;
                    const filteredCheckedIds = currentCheckedIds.filter(
                      (id) => !deletedIdSet.has(`${id}`)
                    );
                    if (
                      filteredCheckedIds.length !== currentCheckedIds.length
                    ) {
                      setCheckedIdsRef.current(filteredCheckedIds);
                    }
                  }

                  setRerender(new Date().getMilliseconds());
                });
            }

            // Fetch dat - skupiny dle id skupin - samostatný dotaz pro každou úroveň zanoření
            groupingRef.current.forEach((_, index) => {
              if (idsForLoadMap[index]?.length > 0) {
                const idsForLoad = idsForLoadMap[index];

                const grouping: DataGroupVO[] = [];
                for (let a = 0; a <= index; a++) {
                  grouping.push(groupingRef.current[a]);
                }

                config
                  .api(
                    {
                      requestType: DataRequestType.GROUP_DATA,
                      filter: queryFilterRef.current,
                      fieldValuesFilter: fieldValuesFilterRef.current,
                      idsRequest: {
                        // @ts-ignore
                        ids: idsForLoad,
                      },
                      grouping: grouping,
                      // @ts-ignore
                      aggregate: aggregateRef.current.map((x) => ({
                        field: x.field,
                        type: x.aggregate.toUpperCase(),
                      })),
                      visibleFields: visibleFieldsRef.current,
                      extendedFilter: extendedFilterRef.current,
                    },
                    { hardFilter: config.hardFilter }
                  )
                  .then((result) => {
                    const resultMap: { [key: string]: DATA } = {};
                    // @ts-ignore
                    result.rows?.forEach((item) => {
                      // @ts-ignore
                      resultMap[`${item.aggregate.grpId}`] = item;
                    });

                    idsForLoad.forEach((id) => {
                      const item = resultMap[`${id}`];
                      setLoadedItemById(true, id, item || null);
                    });

                    setRerender(new Date().getMilliseconds());
                  });
              }
            });
          }
        }
      },
      throttleTime,
      { leading: true, trailing: true }
    ),
    // [config.api]
    [finalTableKeyDataCacheKeyStr]
  );

  const loadMoreRows = useCallback(
    async ({}: /*startIndex, stopIndex*/ IndexRange) => {
      doRowsFetch();
    },
    [config.api, doRowsFetchByIds]
  );

  const onSectionRendered = useCallback((props: SectionRange) => {
    lastRenderedSection.current = props;
    doRowsFetch();
  }, []);

  const getFooter = () => {
    return loadedMap.current[FOOTER];
  };

  const getLoadedItemByIndex = (index: number) => {
    const id = globalData.ids[index];
    if (isGroupByIndex(index)) {
      return loadedMap.current[`${GROUP_PREFIX}${id}`];
    } else {
      return loadedMap.current[`${id}`];
    }
  };

  const isGrouping = () => {
    return rowsData.grouping.grouping.length > 0;
  };

  const isGroupByIndex = (index: number) => {
    return globalData.group[index];
  };

  const isGroupSelectable = (index: number) => {
    const isGroupRow = isGroupByIndex(index);
    if (!isGroupRow) {
      return false;
    }

    const isGroupExpanded =
      getGroupStatusByIndex(index) === GroupStatus.EXPANDED;
    const currentGroupDepth = getDepthByIndex(index);
    const maxGroupDepth = rowsData.grouping?.grouping?.length
      ? rowsData.grouping?.grouping.length - 1
      : 0;

    return currentGroupDepth === maxGroupDepth && isGroupExpanded;
  };

  const getNextGroupHeaderRowIndex = (index: number): number => {
    for (let i = index + 1; i < globalData.group?.length; i++) {
      if (globalData.group[i]) {
        return i;
      }
    }
    return -1;
  };

  const getGroupItemIds = (index: number): ID[] => {
    const isGroupExpanded =
      getGroupStatusByIndex(index) === GroupStatus.EXPANDED;
    if (!isGroupExpanded) {
      return [];
    }
    const ids: ID[] = [];
    const nextGroupHeaderRowIndex = getNextGroupHeaderRowIndex(index);

    for (
      let i = index + 1;
      i <
      (nextGroupHeaderRowIndex !== -1
        ? nextGroupHeaderRowIndex
        : (globalData.ids?.length ?? 0));
      i++
    ) {
      ids.push(globalData.ids[i]);
    }
    return ids;
  };

  const getSelectedGroupItemIds = (index: number): ID[] => {
    const isGroupRow = isGroupByIndex(index);
    if (!isGroupRow) {
      return [];
    }
    const globalCheckedIdsSet = new Set(rowsData.rows.checkedIds);
    const selectedGroupItemsIds = getGroupItemIds(index).filter((itemId) =>
      globalCheckedIdsSet.has(itemId as ID)
    );
    return selectedGroupItemsIds;
  };

  const isGroupChecked = (index: number): boolean => {
    const isGroupRow = isGroupByIndex(index);
    if (!isGroupRow) {
      return false;
    }
    const groupItemIds = getGroupItemIds(index);
    const selectedGroupItemsIds = getSelectedGroupItemIds(index);
    return (
      groupItemIds.length > 0 &&
      groupItemIds.length === selectedGroupItemsIds.length
    );
  };

  const checkGroup = (index: number, checked: boolean) => {
    const isGroupRow = isGroupByIndex(index);
    if (!isGroupRow) {
      return;
    }
    const groupItemIds = getGroupItemIds(index);
    if (checked) {
      rowsData.rows.setCheckedIds([
        ...rowsData.rows.checkedIds,
        ...groupItemIds,
      ] as ID[]);
    } else {
      const groupItemIdsSet = new Set(groupItemIds);
      rowsData.rows.setCheckedIds(
        rowsData.rows.checkedIds.filter(
          (id) => !groupItemIdsSet.has(id)
        ) as ID[]
      );
    }
  };

  const getGroupStatusByIndex = (index: number) => {
    return globalData.groupStatus[index];
  };

  const getDepthByIndex = (index: number) => {
    return globalData.depth[index] || 0;
  };

  const getRowRenderProps = (index: number) => {
    const id = globalData.ids[index];
    const row = getLoadedItemByIndex(index);

    return {
      id,
      isFetching: row === undefined,
      isDeleted: row === null,
      isFetched: row !== undefined,
      row,
      index,
    };
  };

  // Do computedSort dá plný seznam řazení dle skupin (i když v sort není)
  const computedSort = useMemo(() => {
    return computeSort(rowsData.rows.sort, rowsData.grouping.grouping);
  }, [rowsData.rows.sort, rowsData.grouping.grouping]);
  const sortMap: SortMap = useMemo(() => {
    const map: SortMap = {};

    // Do mapy dáme nejdříve skupiny, mají samostatný depth
    computedSort.groupSort.forEach((sort, index) => {
      map[sort.field] = {
        sort: sort,
        depth: index,
        grouped: true,
      };
    });

    // Do mapy dáme ostatní sloupce - opět samostatný depth
    computedSort.colSort.forEach((sort, index) => {
      map[sort.field] = {
        sort: sort,
        depth: index,
        grouped: false,
      };
    });

    return map;
  }, [computedSort, rowsData.rows.sort, rowsData.grouping.grouping]);
  const getSortInfo = (column: FinalTableColumn<DATA>) => {
    if (column.sortable) {
      const field = getColumnField(column);
      if (field) {
        return sortMap[field];
      }
    }

    return undefined;
  };
  const toggleSort = (column: FinalTableColumn<DATA>, shiftKey: boolean) => {
    const multisort = config.multisort ?? true;

    // Pro grouped je sortinfo vždy
    const sortInfo = getSortInfo(column);
    if (sortInfo) {
      // Již se podle toho řadí - toggle
      if (sortInfo.grouped) {
        // Vždy je seřazeno dle všech skupin, jen toggle bez ohledu na shiftKey
        const newGroupSort = [...computedSort.groupSort];
        newGroupSort[sortInfo.depth] = {
          ...newGroupSort[sortInfo.depth],
          asc: !newGroupSort[sortInfo.depth].asc,
        };
        rowsData.rows.setSort([...newGroupSort, ...computedSort.colSort]);
      } else {
        // Bereme ohled na shiftKey
        if (shiftKey && multisort) {
          const newColSort = [...computedSort.colSort];
          newColSort[sortInfo.depth] = {
            ...newColSort[sortInfo.depth],
            asc: !newColSort[sortInfo.depth].asc,
          };
          rowsData.rows.setSort([...computedSort.groupSort, ...newColSort]);
        } else {
          rowsData.rows.setSort([
            ...computedSort.groupSort,
            {
              field: sortInfo.sort.field,
              asc: !sortInfo.sort.asc,
            },
          ]);
        }
      }
    } else {
      // Ještě se podle toho neřadí - týká se jen col, nikoli group, ty existují vždy
      const field = getColumnField(column);
      if (field) {
        if (shiftKey && multisort) {
          rowsData.rows.setSort([
            ...computedSort.groupSort,
            ...computedSort.colSort,
            {
              field,
              asc: true,
            },
          ]);
        } else {
          rowsData.rows.setSort([
            ...computedSort.groupSort,
            {
              field,
              asc: true,
            },
          ]);
        }
      }
    }

    if (rowsData.grouping.grouping.length > 0) {
      setNewDataFetch(true, true);
    } else {
      setNewDataFetch(false, true);
    }
  };
  const getColumnsSortInfo = () => {
    return computedSort.colSort.map((x) => sortMap[x.field]);
  };

  const resetVirtualTableCache = () => {
    infiniteLoaderRef.current?.resetLoadMoreRowsCache(true);
  };

  const getExportDataRequest = (): Partial<DataRequestVO<FILTER>> => {
    return {
      filter: rowsData.filter.queryFilter,
      fieldValuesFilter: rowsData.filter.fieldValuesFilter,
      sort: [...computedSort.groupSort, ...computedSort.colSort],
      extendedFilter: rowsData.filter.queryExtendedFilter,
    };
  };

  // console.log('####sortedVisibleColumnIdsKey', sortedVisibleColumnIdsKey);
  useEffect(() => {
    const dataKey = getFinalTableDataKey([
      rowsData.filter.formFilter,
      rowsData.filter.fieldValuesFilter,
      rowsData.filter.lastFilterChange,
      hardFilterStr,
      extendedFilterStr,
      rowsData.rows.sort,
      sortedVisibleColumnIdsKey,
      rowsData.grouping.grouping,
    ]);

    if (dataKey !== globalData.dataKey) {
      globalData.dataKey = dataKey;

      fetchRef.current = fetchRef.current + 1;
      const currentFetchId = fetchRef.current;

      // TODO znevalidňovat jinde? - nějak to sjednotit
      if (rowsData.grouping.grouping.length > 0) {
        // Grouping - read groups - první úroveň
        config
          .api(
            {
              requestType: DataRequestType.IDS,
              filter: rowsData.filter.queryFilter,
              fieldValuesFilter: rowsData.filter.fieldValuesFilter,
              sort: [...computedSort.groupSort, ...computedSort.colSort],
              // POZOR pouze první úroveň grouping!
              grouping: [rowsData.grouping.grouping[0]],
              // @ts-ignore
              aggregate: rowsData.grouping.aggregate.map((x) => ({
                field: x.field,
                type: x.aggregate.toUpperCase(),
              })),
              extendedFilter: rowsData.filter.queryExtendedFilter,
            },
            { hardFilter: config.hardFilter }
          )
          .then((response) => {
            if (fetchRef.current === currentFetchId) {
              globalData.ids = [];
              globalData.group = [];
              globalData.groupStatus = [];

              const groupIds = response.ids ?? [];
              globalData.ids = groupIds;
              globalData.group = [];
              globalData.groupStatus = [];
              globalData.depth = [];
              for (let a = 0; a < groupIds.length; a++) {
                globalData.group[a] = true;
                globalData.groupStatus[a] = GroupStatus.COLLAPSED;
                globalData.depth[a] = 0;
              }
              globalData.rowsCount = groupIds.length;
              globalData.dataRowsCount = response.totalDataElements;

              setLoading(false);
              setRerender(new Date().getMilliseconds());
              resetVirtualTableCache();
            }
          })
          .catch((error) => {
            if (fetchRef.current === currentFetchId) {
              errorsRef.current.push(getLoadDataError(error));
              setLoading(false);
              setRerender(new Date().getMilliseconds());
            }
          });
      } else {
        // No grouping - read ids
        config
          .api(
            {
              requestType: DataRequestType.IDS,
              filter: rowsData.filter.queryFilter,
              fieldValuesFilter: rowsData.filter.fieldValuesFilter,
              sort: rowsData.rows.sort,
              visibleFields: visibleFields,
              extendedFilter: rowsData.filter.queryExtendedFilter,
            },
            { hardFilter: config.hardFilter }
          )
          .then((response) => {
            if (fetchRef.current === currentFetchId) {
              globalData.ids = [];
              globalData.group = [];
              globalData.groupStatus = [];

              if (response.ids) {
                // @ts-ignore
                globalData.ids = response.ids;
                globalData.group = [];
                globalData.groupStatus = [];
                globalData.depth = [];
                for (let a = 0; a < response.ids.length; a++) {
                  globalData.group[a] = false;
                  globalData.groupStatus[a] = GroupStatus.COLLAPSED;
                  globalData.depth[a] = 0;
                }
                globalData.rowsCount = response.ids.length;
                globalData.dataRowsCount = response.ids.length;
              }
              setLoading(false);
              setRerender(new Date().getMilliseconds());
              resetVirtualTableCache();
            }
          })
          .catch((error) => {
            if (fetchRef.current === currentFetchId) {
              errorsRef.current.push(getLoadDataError(error));
              setLoading(false);
              setRerender(new Date().getMilliseconds());
            }
          });
      }
    } else {
      setLoading(false);
    }
  }, [
    rowsData.filter.formFilter,
    rowsData.filter.fieldValuesFilter,
    rowsData.filter.lastFilterChange,
    rowsData.rows.sort,
    sortedVisibleColumnIdsKey,
    rowsData.grouping.grouping,
    rowsData.grouping.aggregate,
    hardFilterStr,
    extendedFilterStr,
    finalTableKeyDataCacheKeyStr,
  ]);

  const getAggregate = (field?: string) => {
    if (!field) {
      return undefined;
    }
    return rowsData.grouping.aggregate.find((x) => x.field === field);
  };

  const arrayUpdated = (change: ChangeArray) => {
    Object.keys(groupFetchDataRef.current).forEach((key) => {
      let index = groupFetchDataRef.current[key];

      if (index !== undefined) {
        if (change.insert) {
          // Vkládání řádek
          if (index >= change.fromIndex) {
            groupFetchDataRef.current[key] = index + change.count;
          }
        } else {
          // Mazání řádek (např. zabalení)
          if (
            index >= change.fromIndex &&
            index < change.fromIndex + change.count
          ) {
            // Položka je v mazaných, zneplatníme ji
            groupFetchDataRef.current[key] = undefined;
          }
        }
      }
    });

    resetVirtualTableCache();
  };

  const doCollapseChildren = (rowIndex: number) => {
    const depth = globalData.depth[rowIndex];
    const rowsLength = globalData.rowsCount ?? 0;
    let i = rowIndex + 1;
    while (i < rowsLength) {
      if (globalData.depth[i] <= depth) {
        break;
      }
      i++;
    }
    const deleteCount = i - (rowIndex + 1);
    if (deleteCount > 0) {
      // Odebrání z cache načtených objektů
      for (let a = 0; a < deleteCount; a++) {
        delete loadedMap.current[globalData.ids[a + rowIndex + 1]];
      }

      // Odebrání z různých dat
      globalData.ids.splice(rowIndex + 1, deleteCount);
      globalData.group.splice(rowIndex + 1, deleteCount);
      globalData.groupStatus.splice(rowIndex + 1, deleteCount);
      globalData.depth.splice(rowIndex + 1, deleteCount);
      globalData.rowsCount = rowsLength - deleteCount;

      const change: ChangeArray = {
        fromIndex: rowIndex + 1,
        count: deleteCount,
        insert: false,
      };

      arrayUpdated(change);
    }

    globalData.groupStatus[rowIndex] = GroupStatus.COLLAPSED;
    setRerender(new Date().getMilliseconds());
  };

  /**
   * Načtení řádek typu skupina při rozbalení nadřazené skupiny.
   * @param groupId nadřazená skupina
   * @param newDepth hloubka  načtených dat (vlastně depth parent skupiny + 1)
   * @param groupFilter filtr
   * @param grouping dle čeho se seskupuje - pro tuto úroveň načítání! (pokud bude např. seskupovat dle "příjmení", "jména" a "věku" a budu rozbalovat skupinu jméno, bude zde ["příjmení", "jméno"], nikoli věk)
   */
  const doFetchGroupGroupRows = (
    groupId: string,
    newDepth: number,
    groupFilter: GroupFilter,
    grouping: DataGroupVO[]
  ) => {
    config
      .api(
        {
          requestType: DataRequestType.IDS,
          filter: rowsData.filter.queryFilter,
          fieldValuesFilter: rowsData.filter.fieldValuesFilter,
          sort: computedSort.groupSort,
          groupFilter: groupFilter,
          grouping: grouping,
          // @ts-ignore
          aggregate: rowsData.grouping.aggregate.map((x) => ({
            field: x.field,
            type: x.aggregate.toUpperCase(),
          })),
          extendedFilter: extendedFilterRef.current,
        },
        { hardFilter: config.hardFilter }
      )
      .then((response) => {
        const groupRowIndex = groupFetchDataRef.current[groupId];
        if (
          groupRowIndex !== undefined &&
          globalData.groupStatus[groupRowIndex] === GroupStatus.LOADING
        ) {
          const rowIds = response.ids ?? [];
          let index = groupRowIndex + 1;

          arrayMakeSpace(globalData.group, index, response.totalElements);
          arrayMakeSpace(globalData.groupStatus, index, response.totalElements);
          arrayMakeSpace(globalData.depth, index, response.totalElements);
          arrayMakeSpace(globalData.ids, index, response.totalElements);

          for (let a = index; a < index + response.totalElements; a++) {
            globalData.group[a] = true;
            globalData.groupStatus[a] = GroupStatus.COLLAPSED;
            globalData.depth[a] = newDepth;
            globalData.ids[a] = rowIds[a - index];
          }
          globalData.rowsCount =
            (globalData.rowsCount || 0) + response.totalElements;

          globalData.groupStatus[groupRowIndex] = GroupStatus.EXPANDED;

          const change: ChangeArray = {
            fromIndex: index,
            count: response.totalElements,
            insert: true,
          };
          arrayUpdated(change);

          setRerender(new Date().getMilliseconds());
        }
        delete groupFetchDataRef.current[groupId];
      })
      .catch((error) => {
        const groupRowIndex = groupFetchDataRef.current[groupId];
        if (
          groupRowIndex !== undefined &&
          globalData.groupStatus[groupRowIndex] === GroupStatus.LOADING
        ) {
          errorsRef.current.push(getLoadDataError(error));
          setRerender(new Date().getMilliseconds());
        }
      });
  };

  /**
   * Načtení řádek typu data při rozbalení nadřazené skupiny.
   * @param groupId nadřazená skupina
   * @param newDepth hloubka  načtených dat (vlastně depth parent skupiny + 1)
   * @param groupFilter filtr
   */
  const doFetchGroupDataRows = (
    groupId: string,
    newDepth: number,
    groupFilter: GroupFilter
  ) => {
    config
      .api(
        {
          requestType: DataRequestType.IDS,
          filter: rowsData.filter.queryFilter,
          fieldValuesFilter: rowsData.filter.fieldValuesFilter,
          sort: computedSort.colSort,
          groupFilter: groupFilter,
          extendedFilter: extendedFilterRef.current,
        },
        { hardFilter: config.hardFilter }
      )
      .then((response) => {
        const groupRowIndex = groupFetchDataRef.current[groupId];
        if (
          groupRowIndex !== undefined &&
          globalData.groupStatus[groupRowIndex] === GroupStatus.LOADING &&
          response.ids
        ) {
          // @ts-ignore
          const rowIds = response.ids;
          let index = groupRowIndex + 1;
          arrayMakeSpace(globalData.group, index, response.totalElements);
          arrayMakeSpace(globalData.groupStatus, index, response.totalElements);
          arrayMakeSpace(globalData.depth, index, response.totalElements);
          arrayMakeSpace(globalData.ids, index, response.totalElements);

          for (let a = index; a < index + response.totalElements; a++) {
            globalData.group[a] = false;
            globalData.groupStatus[a] = GroupStatus.COLLAPSED;
            globalData.depth[a] = newDepth;
            globalData.ids[a] = rowIds[a - index];
          }
          globalData.rowsCount =
            (globalData.rowsCount || 0) + response.totalElements;

          globalData.groupStatus[groupRowIndex] = GroupStatus.EXPANDED;

          const change: ChangeArray = {
            fromIndex: index,
            count: response.totalElements,
            insert: true,
          };
          arrayUpdated(change);

          setRerender(new Date().getMilliseconds());
        }
        delete groupFetchDataRef.current[groupId];
      })
      .catch((error) => {
        const groupRowIndex = groupFetchDataRef.current[groupId];
        if (
          groupRowIndex !== undefined &&
          globalData.groupStatus[groupRowIndex] === GroupStatus.LOADING
        ) {
          errorsRef.current.push(getLoadDataError(error));
          setRerender(new Date().getMilliseconds());
        }
      });
  };

  /**
   * Fetch podřízených položek skupiny, buď dalších skupin nebo datových řádků.
   */
  const doFetchGroupChildren = (rowIndex: number, rowData: DATA) => {
    // @ts-ignore
    const groupId: string = rowData?.aggregate.grpId;
    const depth = globalData.depth[rowIndex];

    groupFetchDataRef.current[groupId] = rowIndex;

    const groupFilter: GroupFilter = {};
    const grouping: DataGroupVO[] = [];
    groupId.split('»').forEach((value, index) => {
      let dataGroup = rowsData.grouping.grouping[index];
      grouping.push(dataGroup);

      // Do filtru na group - o jednu méně než grouping
      if (index < depth + 1) {
        const field = dataGroup.field;
        // @ts-ignore
        groupFilter[field] = value === NULL_VALUE ? null : value;
      }
    });

    // Do grouping +1 depth
    if (depth + 1 < rowsData.grouping.grouping.length) {
      grouping.push(rowsData.grouping.grouping[depth + 1]);
    }

    // Fetch dat
    if (depth + 1 < rowsData.grouping.grouping.length) {
      // Načtení podřízených group
      doFetchGroupGroupRows(groupId, depth + 1, groupFilter, grouping);
    } else {
      // Načtení podřízených datových řádků
      doFetchGroupDataRows(groupId, depth + 1, groupFilter);
    }
  };

  const toggleExpand = (rowIndex: number) => {
    switch (globalData.groupStatus[rowIndex]) {
      case GroupStatus.COLLAPSED:
        {
          let rowData = getLoadedItemByIndex(rowIndex);
          if (rowData) {
            globalData.groupStatus[rowIndex] = GroupStatus.LOADING;
            doFetchGroupChildren(rowIndex, rowData);
          }
        }
        break;
      case GroupStatus.EXPANDED:
        {
          let rowData = getLoadedItemByIndex(rowIndex);
          if (rowData) {
            doCollapseChildren(rowIndex);
          }
        }
        break;
      case GroupStatus.LOADING:
        globalData.groupStatus[rowIndex] = GroupStatus.COLLAPSED;
        break;
    }
    setRerender(new Date().getMilliseconds());
  };

  /**
   * Nastaví stav tak, aby se překreslilo jako že už se budou načítat  nová data.
   */
  const setNewDataFetch = (resetData: boolean, scrollToTop: boolean) => {
    setLoading(true);

    errorsRef.current = [];

    // Pokud by se něco rozbalovalo, tak už to nechceme
    groupFetchDataRef.current = {};

    if (resetData) {
      globalData.rowsCount = undefined;
      loadedMap.current = {};
    }

    if (scrollToTop) {
      rowsData.rows.setLastVisitedRowIndex(0);
    }
    setRerender(new Date().getMilliseconds());
  };

  const addGroup = (field: string) => {
    const newGrouping = [
      ...rowsData.grouping.grouping,
      {
        field: field,
      },
    ];
    delete loadedMap.current[FOOTER];
    groupingRef.current = newGrouping;
    rowsData.grouping.setGrouping(newGrouping);

    setNewDataFetch(true, true);
  };

  const removeGroup = (field: string) => {
    const newGrouping = rowsData.grouping.grouping.filter(
      (x) => x.field !== field
    );
    delete loadedMap.current[FOOTER];
    groupingRef.current = newGrouping;
    rowsData.grouping.setGrouping(newGrouping);

    setNewDataFetch(true, true);
  };

  const resetRowsCache = ({
    dataRow,
    groupRow,
    footer,
  }: {
    dataRow?: boolean;
    groupRow?: boolean;
    footer?: boolean;
  }) => {
    // Reset všeho
    if (dataRow && groupRow && footer) {
      loadedMap.current = {};
      setRefetchFooter(new Date().getMilliseconds());
      return;
    }

    // Reset dle typu řádek
    Object.keys(loadedMap.current).forEach((k) => {
      if (k.startsWith(GROUP_PREFIX)) {
        if (groupRow) {
          delete loadedMap.current[k];
        }
      } else {
        if (dataRow) {
          delete loadedMap.current[k];
        }
      }
    });

    // Reset footer
    if (footer) {
      delete loadedMap.current[FOOTER];
      setRefetchFooter(new Date().getMilliseconds());
    }
  };

  const setAggregate = (field: string, aggregate?: DataAggregateVO) => {
    const newAggregate = rowsData.grouping.aggregate.filter(
      (x) => x.field !== field
    );

    delete loadedMap.current[FOOTER];
    if (aggregate) {
      newAggregate.push(aggregate);
    }
    aggregateRef.current = newAggregate;
    rowsData.grouping.setAggregate(newAggregate);

    // ---

    // Pokud děláme fetch group dat, tak vše vyresetujeme a znovu načteme, pokud ho neděláme, jen dáme invalidate všech dat
    if (groupingRef.current.length > 0) {
      resetRowsCache({ groupRow: true });
      resetVirtualTableCache();
    }
  };

  const setFieldValuesFilter = (fieldValuesFilter: FieldValuesFilterData) => {
    rowsData.filter.setFieldValuesFilter(fieldValuesFilter);
    fieldValuesFilterRef.current = fieldValuesFilter;

    globalData.dataRowsCount = undefined;

    setNewDataFetch(true, true);
  };

  const setFilter = (formData: FILTER, queryData: FILTER) => {
    rowsData.filter.setFilter(formData, queryData);

    globalData.dataRowsCount = undefined;

    setNewDataFetch(true, true);
  };

  const setShowFooter = (showFooter: boolean) => {
    if (showFooter !== rowsData.footer.showFooter) {
      rowsData.footer.setShowFooter(showFooter);
      delete loadedMap.current[FOOTER];
    }
  };

  const setProfile = (profile?: FinalTableProfileData) => {
    let sort, grouping, aggregate, showFooter, columns, fieldValuesFilter;
    if (profile) {
      sort = profile.sort;
      grouping = profile.grouping;
      aggregate = profile.aggregate;
      showFooter = profile.showFooter;
      columns = profile.columns;
      fieldValuesFilter = profile.fieldValuesFilter;
    } else {
      const {
        initialSort = [],
        initialGrouping = [],
        initialAggregate = [],
        initialShowFooter = false,
        initialColumns = [],
        initialFieldValuesFilter = {},
      } = config?.initialConfig || {};
      sort = initialSort;
      grouping = initialGrouping;
      aggregate = initialAggregate;
      showFooter = initialShowFooter;
      columns = initialColumns;
      fieldValuesFilter = initialFieldValuesFilter;
    }

    const newSortedVisibleColumnIdsKey = getVisibleColumnsKey(
      columns,
      config.columns,
      grouping
    );

    const newDataKey = getFinalTableDataKey([
      rowsData.filter.formFilter,
      rowsData.filter.lastFilterChange,
      hardFilterStr,
      extendedFilterStr,
      sort,
      newSortedVisibleColumnIdsKey,
      grouping,
    ]);

    //nastavim řazení
    rowsData.rows.setSort(sort);

    //nastavim filtr dle hodnot sloupců
    rowsData.filter.setFieldValuesFilter(fieldValuesFilter);

    //nastavim footer
    setShowFooter(showFooter);

    //nastavim groupy
    groupingRef.current = grouping;
    rowsData.grouping.setGrouping(grouping);

    //nastavim agregace
    aggregateRef.current = aggregate;
    rowsData.grouping.setAggregate(aggregate);

    //nastavim sirku sloupcu a jejich viditelnost
    rowsData.columns.setColumns(columns);

    if (profile && profile.groupColumnWidth) {
      setGroupColumnWidth(profile.groupColumnWidth);
    }

    //refresh dat
    if (newDataKey !== globalData.dataKey) {
      setNewDataFetch(true, true);
    } else {
      setRerender(new Date().getMilliseconds());
    }
  };

  /**
   * Nastavení sloupců. Pokud se změnilo pořadí pro seskupené sloupce, tak řadí a nastavuje i grouping data.
   * @param columns
   */
  const setColumns = (columns: ColumnInfo[]) => {
    // Vyřešení seskupených sloupců
    const groupedColumns = rowsData.grouping.grouping.map((x) => x.field);
    const newGroupedColumns = columns
      .filter((x) => x.visible && groupedColumns.indexOf(x.ident) >= 0)
      .map((x) => x.ident);

    let newGrouping = rowsData.grouping.grouping;
    if (groupedColumns.join('|') !== newGroupedColumns.join('|')) {
      // Změna v pořadí seskupení sloupců
      newGrouping = [...rowsData.grouping.grouping].sort((a, b) => {
        return (
          newGroupedColumns.indexOf(a.field) -
          newGroupedColumns.indexOf(b.field)
        );
      });
    }

    // ---

    const currKey = getVisibleColumnsKey(
      rowsData.columns.columns,
      config.columns,
      rowsData.grouping.grouping
    );
    const newKey = getVisibleColumnsKey(columns, config.columns, newGrouping);

    if (newGrouping !== rowsData.grouping.grouping) {
      delete loadedMap.current[FOOTER];
      groupingRef.current = newGrouping;
      rowsData.grouping.setGrouping(newGrouping);
      rowsData.columns.setColumns(columns);
      setNewDataFetch(true, true);
    } else if (newKey !== currKey) {
      rowsData.columns.setColumns(columns);
      setNewDataFetch(true, false);
    } else {
      rowsData.columns.setColumns(columns);
      setRerender(new Date().getMilliseconds());
    }
  };

  const setGroupColumnWidth = (groupWidth: number) => {
    rowsData.grouping.setGroupColumnWidth(groupWidth);
  };

  const searchFieldValues = (
    fieldValuesRequest: DataFieldValuesRequestVO
  ): Promise<DataResponseVO<DATA>> => {
    const column = getColumnByField(fieldValuesRequest.field, config.columns);
    const sortable = column.sortable === undefined ? true : column.sortable;
    return new Promise((resolve, reject) => {
      config
        .api(
          {
            requestType: DataRequestType.FIELD_VALUES,
            filter: rowsData.filter.queryFilter,
            sort: sortable
              ? [{ field: fieldValuesRequest.field, asc: true }]
              : [],
            rangeRequest: {
              firstResult: 0,
              maxResults:
                (config.fieldValuesFilterMaxFetchedItems ||
                  DEFAULT_FIELD_VALUES_FILTER_MAX_FETCHED_ITEMS) + 1,
            },
            fieldValuesRequest,
            extendedFilter: rowsData.filter.queryExtendedFilter,
          },
          { hardFilter: config.hardFilter }
        )
        .then((result) => {
          resolve(result);
        })
        .catch(reject);
    });
  };

  // RESET načtených dat při změně klíče tabulky (pokud je to případ kdy se provádí jen rerender tabulky, ale s jiným klíčem)
  const firstInitTableRef = useRef(true);
  useEffect(() => {
    if (firstInitTableRef.current) {
      firstInitTableRef.current = false;
      return;
    }
    resetRowsCache({
      dataRow: true,
      groupRow: true,
      footer: true,
    });
    resetVirtualTableCache();
  }, [finalTableKeyDataCacheKeyStr]);

  // ---
  firstRenderRef.current = false;

  return {
    config,
    loading,
    showFooter: rowsData.footer.showFooter,
    setShowFooter: setShowFooter,
    checkedIds: rowsData.rows.checkedIds,
    setCheckedIds: rowsData.rows.setCheckedIds,
    columns: rowsData.columns.columns,
    setColumns: setColumns,
    groupColumnWidth: rowsData.grouping.groupColumnWidth,
    setGroupColumnWidth: setGroupColumnWidth,
    hardFilter: config.hardFilter,
    formExtendedFilter: rowsData.filter.formExtendedFilter,
    queryExtendedFilter: rowsData.filter.queryExtendedFilter,
    setExtendedFilter: rowsData.filter.setExtendedFilter,
    // @ts-ignore
    formFilter: rowsData.filter.formFilter,
    // @ts-ignore
    queryFilter: rowsData.filter.queryFilter,
    fieldValuesFilter: rowsData.filter.fieldValuesFilter,
    filterVisibleFields: rowsData.filter.visibleFields,
    setFilterVisibleFields: rowsData.filter.setVisibleFields,
    getClearFilter: rowsData.filter.getClearFilter,
    setLastVisitedRowIndex: rowsData.rows.setLastVisitedRowIndex,
    lastVisitedRowIndex: rowsData.rows.lastVisitedRowIndex,
    setFilter: setFilter,
    setFieldValuesFilter: setFieldValuesFilter,
    setSort: rowsData.rows.setSort,
    setProfile: setProfile,
    searchFieldValues: searchFieldValues,
    sort: rowsData.rows.sort,
    getSortInfo,
    toggleSort,
    getColumnsSortInfo,
    ids: globalData.ids,
    group: globalData.group,
    depth: globalData.depth,
    grouping: rowsData.grouping.grouping,
    aggregate: rowsData.grouping.aggregate,
    setAggregate: setAggregate,
    getAggregate,
    totalElements: globalData.rowsCount,
    totalDataElements: globalData.dataRowsCount,
    rerender,
    //@ts-ignore
    infiniteLoaderRef,
    loadMoreRows,
    onSectionRendered,
    isRowLoaded,
    getRowByIndex: getLoadedItemByIndex,
    getFooter,
    getRowRenderProps,
    getGroupStatusByIndex,
    isGrouping,
    isGroupByIndex,
    isGroupSelectable,
    getNextGroupHeaderRowIndex,
    getGroupItemIds,
    getSelectedGroupItemIds,
    isGroupChecked,
    checkGroup,
    toggleExpand,
    getDepthByIndex,
    addGroup,
    removeGroup,
    getExportDataRequest,
    errors: errorsRef.current,
    columnsOrder: columnsView.meta.sortedColumns
      .map((x) => x.id || x.accessor || '')
      .filter((x) => x !== ''),
    visibleColumns: columnsView.meta.visibleColumns
      .map((x) => x.id || x.accessor || '')
      .filter((x) => x !== ''),
  };
};
